import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { Menu, X, ChevronDown, LogOut, Settings, BookOpen, BarChart3, Ticket } from 'lucide-react';
import { useAuthStore } from '../store/auth';

// Premium Logo - Black/White Gradient
const Logo = ({ className = '' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="12" fill="url(#logo-grad-main)" />
    <path d="M12 14H18V16H14V24H18V26H12V14ZM22 14H28V26H22V24H26V16H22V14ZM16 18H24V22H16V18Z" fill="white" />
    <defs>
      <linearGradient id="logo-grad-main" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
        <stop stopColor="#2d2d2d" />
        <stop offset="1" stopColor="#0a0a0a" />
      </linearGradient>
    </defs>
  </svg>
);

export default function MainLayout() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const dashboardPath = user?.role === 'PROFESSOR' ? '/professor' : '/student';

  return (
    <div className="min-h-screen bg-void flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 glass">
        <nav className="container-luxury h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 focus:outline-none focus-visible:ring-0">
            <Logo className="w-9 h-9" />
            <span className="font-semibold text-lg text-white">Ulearning</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/courses" 
              className={`text-sm font-medium transition-colors ${
                location.pathname === '/courses' 
                  ? 'text-white' 
                  : 'text-white/50 hover:text-white'
              }`}
            >
              Browse Courses
            </Link>
          </div>

          {/* Auth Buttons / Profile */}
          <div className="hidden md:flex items-center gap-3">
            {isAuthenticated && user ? (
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full 
                           bg-white/[0.03] hover:bg-white/[0.06] 
                           border border-white/[0.08] transition-all duration-200"
                >
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={user.firstName}
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full flex items-center justify-center 
                                  text-white text-xs font-semibold"
                         style={{ background: 'linear-gradient(135deg, #2a4a5b 0%, #6c7e8d 100%)' }}>
                      {user.firstName.charAt(0)}
                    </div>
                  )}
                  <span className="text-white text-sm font-medium">{user.firstName}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-white/40 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
                </button>

                <AnimatePresence>
                  {isProfileOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 bg-void-100/95 backdrop-blur-xl 
                               border border-white/[0.08] rounded-xl shadow-glass overflow-hidden"
                    >
                      {/* User info */}
                      <div className="px-4 py-3 border-b border-white/[0.06]">
                        <p className="text-white font-medium text-sm">{user.firstName} {user.lastName}</p>
                        <p className="text-white/40 text-xs truncate">{user.email}</p>
                      </div>

                      <div className="p-1.5">
                        <Link
                          to={dashboardPath}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.05] transition-colors"
                        >
                          <BarChart3 className="w-4 h-4 text-white/40" />
                          <span className="text-white/80 text-sm">Dashboard</span>
                        </Link>
                        <Link
                          to={`${dashboardPath}/my-courses`}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.05] transition-colors"
                        >
                          <BookOpen className="w-4 h-4 text-white/40" />
                          <span className="text-white/80 text-sm">My Courses</span>
                        </Link>
                        <Link
                          to={`${dashboardPath}/settings`}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.05] transition-colors"
                        >
                          <Settings className="w-4 h-4 text-white/40" />
                          <span className="text-white/80 text-sm">Settings</span>
                        </Link>
                        <Link
                          to={`${dashboardPath}/support`}
                          onClick={() => setIsProfileOpen(false)}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.05] transition-colors"
                        >
                          <Ticket className="w-4 h-4 text-white/40" />
                          <span className="text-white/80 text-sm">Support</span>
                        </Link>
                      </div>
                      
                      <div className="border-t border-white/[0.06] p-1.5">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg 
                                   hover:bg-coral/10 transition-colors group"
                        >
                          <LogOut className="w-4 h-4 text-white/40 group-hover:text-coral transition-colors" />
                          <span className="text-white/80 text-sm group-hover:text-coral transition-colors">Logout</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <>
                <Link to="/login" className="btn-ghost">
                  Sign In
                </Link>
                <Link to="/register" className="btn-primary">
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-5 h-5 text-white/70" />
            ) : (
              <Menu className="w-5 h-5 text-white/70" />
            )}
          </button>
        </nav>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/[0.06] overflow-hidden"
            >
              <div className="container-luxury py-4 space-y-2">
                <Link
                  to="/courses"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-2.5 rounded-lg text-white/70 hover:text-white 
                           hover:bg-white/[0.05] transition-colors"
                >
                  Browse Courses
                </Link>
                
                {isAuthenticated && user ? (
                  <>
                    <Link
                      to={dashboardPath}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block px-4 py-2.5 rounded-lg text-white/70 hover:text-white 
                               hover:bg-white/[0.05] transition-colors"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }}
                      className="w-full text-left px-4 py-2.5 rounded-lg text-error/80 
                               hover:text-error hover:bg-error/10 transition-colors"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <div className="flex gap-3 pt-2">
                    <Link
                      to="/login"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="btn-secondary flex-1 justify-center"
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="btn-primary flex-1 justify-center"
                    >
                      Get Started
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/[0.06]">
        <div className="container-luxury py-10 sm:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center gap-2.5 focus:outline-none focus-visible:ring-0">
                <Logo className="w-9 h-9" />
                <span className="font-semibold text-lg text-white">Ulearning</span>
              </Link>
              <p className="mt-4 text-white/40 text-sm max-w-sm leading-relaxed">
                The premier learning platform for ambitious professionals. 
                Master new skills from industry experts.
              </p>
            </div>

            {/* Links */}
            <div>
              <h3 className="font-semibold text-white text-sm mb-4">Platform</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/courses" className="text-white/40 hover:text-white text-sm transition-colors">
                    Browse Courses
                  </Link>
                </li>
                <li>
                  <Link to="/become-instructor" className="text-white/40 hover:text-white text-sm transition-colors">
                    Become Instructor
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-white/40 hover:text-white text-sm transition-colors">
                    Pricing
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-white text-sm mb-4">Support</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/help" className="text-white/40 hover:text-white text-sm transition-colors">
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white/40 hover:text-white text-sm transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-white/40 hover:text-white text-sm transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-white/40 hover:text-white text-sm transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-white/[0.06] text-center">
            <p className="text-white/30 text-sm">
              © {new Date().getFullYear()} Ulearning. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
